import { useState } from "react";
import { Dayjs } from "dayjs";
import DateRangePicker from "./components/DateRangePickerComponent";
import { fetchGetDetalleVenta, fetchGetReporteVentas, VentaDetalleItem } from "../services/reporteService";
import { useUserContext } from "../hooks/UserContext";
import DynamicGrid from "../components/DynamicTable";
import { reporteVentasGridColumnDefinitions, ReporteVentasModel, transformReporteVentasData } from "../models/ReporteVentasModel";
import LocalSelector from "./components/LocalSelector";
import { CircularProgress, Button, Box, Grid, Typography, Paper, Stack, Divider, Dialog, DialogContent, DialogTitle, Alert } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { formatNumberAsEsAR } from "../utils/CurrencyFormatter";
import { columnasDetalleVenta } from "../components/detalleColumnas";

type ReporteVentasTotals = {
  total: number;
  descuento: number;
  recargo: number;
};

const emptyTotals = (): ReporteVentasTotals => ({ total: 0, descuento: 0, recargo: 0 });

const calculateTotals = (data: ReporteVentasModel[]) => {
  return data.reduce((acc, item) => {
    acc.total += item.total || 0;
    acc.descuento += item.descuento || 0;
    acc.recargo += item.recargo || 0;
    return acc;
  }, emptyTotals());
};

const calculateSumByFormaDePago = (data: ReporteVentasModel[]) => {
  return data.reduce((acc, item) => {
    const key = item.formadepago;
    if (!acc[key]) {
      acc[key] = emptyTotals();
    }
    acc[key].total += item.total || 0;
    acc[key].descuento += item.descuento || 0;
    acc[key].recargo += item.recargo || 0;
    return acc;
  }, {} as Record<string, ReporteVentasTotals>);
};

const metricSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  p: 1.5,
  bgcolor: "background.default",
};

const ReporteVentas = () => {
  const { getUser } = useUserContext();
  const [idLocal, setIdLocal] = useState<string | undefined>(undefined);
  const [loadingReporte, setLoadingReporte] = useState(false);
  const [reporteVentas, setReporteVentas] = useState<ReporteVentasModel[]>([]);
  const [detalleVenta, setDetalleVenta] = useState<VentaDetalleItem[]>([]);
  const [detalleAbierto, setDetalleAbierto] = useState(false);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState<string | null>(null);
  const [selectedRange, setSelectedRange] = useState<{ startDate: Dayjs | null; endDate: Dayjs | null }>({
    startDate: null,
    endDate: null,
  });

  const handleDateChange = (range: { startDate: Dayjs | null; endDate: Dayjs | null }) => {
    setSelectedRange(range);
  };

  const handleLocalChange = (idLocal: string | undefined) => {
    setIdLocal(idLocal);
  };

  const getReporteDeVentas = () => {
    if (idLocal && selectedRange.startDate && selectedRange.endDate) {
      setLoadingReporte(true);
      fetchGetReporteVentas(getUser()!, selectedRange.startDate!, selectedRange.endDate!, Number(idLocal))
        .then((res: ReporteVentasModel[]) => {
          const transformedData = transformReporteVentasData(res); // Transform the data
          setReporteVentas(transformedData);
          setLoadingReporte(false);
        })
        .catch((err) => {
          setLoadingReporte(false);
        });
    }
  };

  const abrirDetalleVenta = async (evento: any) => {
    const venta = evento.row as ReporteVentasModel;
    const usuario = getUser();
    if (!usuario) return;
    setDetalleAbierto(true);
    setCargandoDetalle(true);
    setErrorDetalle(null);
    try {
      setDetalleVenta(await fetchGetDetalleVenta(usuario, venta.id, venta.idlocal, String(venta.tipo).toLowerCase().includes("proforma")));
    } catch {
      setDetalleVenta([]);
      setErrorDetalle("No se pudo cargar el detalle de la venta.");
    } finally {
      setCargandoDetalle(false);
    }
  };

  const totals = calculateTotals(reporteVentas);
  const sumByFormaDePago = calculateSumByFormaDePago(reporteVentas);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Reporte de Ventas
      </Typography>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <DateRangePicker onDateChange={handleDateChange} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <LocalSelector onLocalChange={handleLocalChange} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Button
            onClick={getReporteDeVentas}
            variant="contained"
            color="primary"
            fullWidth
            disabled={loadingReporte}
            startIcon={loadingReporte ? <CircularProgress color="inherit" size={18} /> : undefined}
            sx={{ height: "100%" }}
          >
            {loadingReporte ? "Obteniendo..." : "Obtener Reporte"}
          </Button>
        </Grid>
      </Grid>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>Resumen</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4}>
            <Box sx={metricSx}>
              <Typography variant="caption" color="text.secondary">Ventas</Typography>
              <Typography variant="h6">{formatNumberAsEsAR(totals.total)}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={metricSx}>
              <Typography variant="caption" color="text.secondary">Descuentos</Typography>
              <Typography variant="h6" color="error.main">{formatNumberAsEsAR(totals.descuento)}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={metricSx}>
              <Typography variant="caption" color="text.secondary">Recargos</Typography>
              <Typography variant="h6" color="success.main">{formatNumberAsEsAR(totals.recargo)}</Typography>
            </Box>
          </Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Por forma de pago</Typography>
        <Stack spacing={1}>
          {Object.entries(sumByFormaDePago).map(([key, value]) => (
            <Box
              key={key}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1.4fr repeat(3, 1fr)" },
                gap: 1,
                alignItems: "center",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                px: 1.5,
                py: 1,
              }}
            >
              <Typography fontWeight={600}>{key}</Typography>
              <Typography><Box component="span" color="text.secondary">Total </Box>{formatNumberAsEsAR(value.total)}</Typography>
              <Typography color="error.main"><Box component="span" color="text.secondary">Desc. </Box>{formatNumberAsEsAR(value.descuento)}</Typography>
              <Typography color="success.main"><Box component="span" color="text.secondary">Rec. </Box>{formatNumberAsEsAR(value.recargo)}</Typography>
            </Box>
          ))}
        </Stack>
      </Paper>
      <DynamicGrid
        data={reporteVentas}
        customHeaderMap={reporteVentasGridColumnDefinitions}
        pageSize={10}
        removeIfNotCustom={true}
        rowClickEventHandler={abrirDetalleVenta}
      />
      <Dialog open={detalleAbierto} onClose={() => setDetalleAbierto(false)} fullWidth maxWidth="lg">
        <DialogTitle>Detalle de venta</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            La forma y su equivalencia son las registradas en la operación. La cantidad base corresponde a las unidades del producto; operaciones antiguas pueden no tener forma registrada.
          </Typography>
          {cargandoDetalle ? <CircularProgress /> : errorDetalle ? <Alert severity="error">{errorDetalle}</Alert> : (
            <DataGrid autoHeight rows={detalleVenta.map((item, index) => ({ ...item, id: index }))} columns={columnasDetalleVenta} pageSizeOptions={[10, 20]} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ReporteVentas;
